import * as React from 'react';
import * as Classes from '../common/classes';
import classNames from 'classnames';
import { Icon } from '../icon/Icon';
import { ReactNode, useEffect, useRef } from 'react';
import { StringInput } from '../string/StringInput';
import { List } from '../list/List';
import { ListItem } from '../list/ListItem';
import {
  defaultMultiSelectRenderItem,
  MultiSelectItem,
} from './MultiSelectSearch';
import { useQuery } from 'react-query';
import { Spinner } from '../spinner/Spinner';
import { Title } from '../typography/Title';
import { Space } from '../space/Space';
import { Tag } from '../tag/Tag';
import { debounce } from 'lodash-es';
import { mcClose, mcSearch } from '@mc/react-ui-icons';
import { Button } from '../button/Button';
import { Callout } from '../callout/Callout';
import { isMobile } from '../common/utils';

type Props<ITEM extends MultiSelectItem> = {
  getLabel?: (item: ITEM) => string;
  renderItem?: (item: ITEM) => string | ReactNode;
  onChange: (val: ITEM[]) => void;
  value: ITEM[];
  className?: string;
  searchItems: (searchText: string) => Promise<ITEM[]>;
  getItemsByIds?: (ids: (number | string)[]) => Promise<ITEM[]>;
  /**
   * Maximální počet zobrazených položek v seznamu při filtrování, při neuvedení je hodnota 100.
   */
  maxItemCount?: number;
  /**
   * Položka byla vybrána v seznamu hodnot a uživatel ji ze seznamu odebral.
   * @param val odebraná položka
   */
  onRemove?: (val: ITEM) => void;
  title?: ReactNode;
  closePopup?: () => void;
};

export function ApiMultiSelectSearch<ITEM extends MultiSelectItem>({
  onChange,
  value = [],
  renderItem = defaultMultiSelectRenderItem,
  searchItems,
  title,
  className,
  closePopup,
  onRemove,
  maxItemCount = 100,
}: Props<ITEM>) {
  const [search, setSearch] = React.useState('');
  const [searchInputText, setSearchInputText] = React.useState('');
  const fixedVal = Array.isArray(value) ? value : [];
  const checkedMap: any = {};
  fixedVal.forEach((val, index) => {
    checkedMap[val.id] = index;
  });

  const debouncedSearch = React.useCallback(
    debounce((text: any) => {
      setSearch(text);
    }, 500),
    []
  );

  const itemsQuery = useQuery<ITEM[]>(
    ['api-multiselect-search', search],
    async () => await searchItems(search),
    { enabled: !!search }
  );

  const inputRef = useRef<any>();

  const realItems: ITEM[] = itemsQuery.data || [];

  // Pokud focus nastavíme bez timeoutu, je z nějakého neznámého důvodu rozmazaný font v celém popup okně - ale pouze v Chromium
  useEffect(() => {
    setTimeout(() => {
      inputRef.current.focus();
    }, 1);
  }, [inputRef.current]);

  const fetchingData =
    (!itemsQuery.data && itemsQuery.isFetching) || !itemsQuery.isFetched;

  const remove = (i: ITEM) => {
    for (let index = 0; index < value.length; index++) {
      if (value[index].id === i.id) {
        onChange([...fixedVal.slice(0, index), ...fixedVal.slice(index + 1)]);
        if (onRemove) {
          onRemove(i);
        }
        break;
      }
    }
  };

  return (
    <div
      className={classNames(className, Classes.API_MULTI_SELECT_SEARCH)}
      style={{
        width: 800,
      }}
    >
      <div className={classNames(Classes.API_MULTI_SELECT_SEARCH_LEFT)}>
        <Title
          className={Classes.MULTI_SELECT_SEARCH_TITLE}
          extra={
            <Button
              onClick={closePopup}
              style={'link'}
              icon={<Icon icon={mcClose} />}
            />
          }
          type={'card'}
        >
          {title}
        </Title>
        <StringInput
          inputRef={inputRef}
          placeholder="Vyhledat"
          value={searchInputText}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchInputText(e.target.value);
            debouncedSearch(e.target.value);
          }}
          rightElement={<Icon fixedWidth icon={mcSearch} />}
          className={Classes.API_MULTI_SELECT_SEARCH_INPUT}
        />
        {maxItemCount && realItems?.length > maxItemCount && (
          <Callout intent={'info'} className={'mt-1'}>
            {`Zobrazeno prvních ${maxItemCount} záznamů`}
          </Callout>
        )}
        <div
          className={classNames(
            Classes.API_MULTI_SELECT_SEARCH_ITEMS_CONTAINER,
            'mt-1'
          )}
        >
          <List mode={'input'} compact>
            {fetchingData && search && <Spinner className={'p-2'} />}
            {!search && (
              <div className={'p-2'}>Zadejte filtr pro vyhledání hodnot</div>
            )}
            {!fetchingData && (
              <>
                {realItems.length === 0 && (
                  <div className={'p-2'}>
                    Dle zadaného filtru nebyly nalezeny žádné položky
                  </div>
                )}
                {realItems
                  .filter((_, index) => !maxItemCount || index < maxItemCount)
                  .map((item) => {
                    const index = checkedMap[item.id];
                    const selected = index !== undefined;
                    const onSelect = selected
                      ? () => {
                          onChange([
                            ...fixedVal.slice(0, index),
                            ...fixedVal.slice(index + 1),
                          ]);
                          if (onRemove) {
                            onRemove(item);
                          }
                        }
                      : () => {
                          // Zde by případně mělo být vložení hodnoty tak, aby výsledek byl správně seřazen
                          onChange([...fixedVal, item]);
                        };
                    return (
                      <ListItem
                        // focus={!disableKeyboard && focus && activeIndex === listIndex}
                        rowCheck
                        checkable
                        checked={selected}
                        onCheck={onSelect}
                      >
                        {renderItem(item)}
                      </ListItem>
                    );
                  })}
              </>
            )}
          </List>
        </div>
      </div>
      <div className={classNames(Classes.API_MULTI_SELECT_SEARCH_RIGHT)}>
        <div className={Classes.API_MULTI_SELECT_SEARCH_TAGS_CONTAINER}>
          <Space vertical={isMobile} wrap={!isMobile} size={['1', '2']}>
            {value &&
              value.map((v) => {
                return (
                  <Tag
                    size={'lg'}
                    heigth={'inherit'}
                    closable
                    onClose={() => remove(v)}
                  >
                    {renderItem(v)}
                  </Tag>
                );
              })}
          </Space>
        </div>
      </div>
    </div>
  );
}
